import React, { useMemo, useState } from 'react';
import { Table, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { FrontsiteOrder, FrontsiteOrderItem } from '../../../types';
import { maskEmail, maskPhone } from '../utils';

interface FrontsiteOrderDetailsProps {
  order: FrontsiteOrder | null;
  formatCurrency: (value: number) => string;
  formatExactCurrency: (value: number) => string;
}

const formatDateLabel = (value?: string) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString();
};

const buildLocationLabel = (order: FrontsiteOrder | null) => {
  if (!order) return 'N/A';
  const parts = [order.city, order.state, order.country?.short_name];
  return parts.filter(Boolean).join(', ') || order.address || 'N/A';
};

const FrontsiteOrderDetails: React.FC<FrontsiteOrderDetailsProps> = ({
  order,
  formatCurrency,
  formatExactCurrency,
}) => {
  // ✅ Early return BEFORE any hooks
  if (!order) {
    return (
      <div className="p-4 text-center text-slate-500 text-sm">
        Select an order to view its details.
      </div>
    );
  }

  // Now declare all hooks
  const [searchValue, setSearchValue] = useState('');
  const [sortState, setSortState] = useState<{ key?: string; order?: 'ascend' | 'descend' }>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const totalAmount = Number(order.total) || 0;
  const subtotalAmount = Number(order.subtotal) || 0;
  const shippingCharge = Number(order.shipping_charge) || 0;

  const maskedEmail = order.email ? maskEmail(order.email) : 'N/A';
  const maskedPhone = order.phonenumber ? maskPhone(order.phonenumber) : 'N/A';

  const infoCards = [
    { 
      label: 'Company', 
      value: order.company || 'N/A',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700'
    },
    { 
      label: 'Email', 
      value: maskedEmail,
      title: order.email || undefined,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-700'
    },
    { 
      label: 'Phone', 
      value: maskedPhone,
      title: order.phonenumber || undefined,
      bgColor: 'bg-green-100',
      textColor: 'text-green-700'
    },
    { 
      label: 'Sales Rep', 
      value: order.sales_representative || 'N/A',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700'
    },
    { 
      label: 'Order Date', 
      value: formatDateLabel(order.created_at),
      bgColor: 'bg-cyan-100',
      textColor: 'text-cyan-700'
    },
    { 
      label: 'Shipping Status', 
      value: order.ship_status || 'N/A',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700'
    },
    { 
      label: 'Location', 
      value: buildLocationLabel(order),
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-700'
    },
    {
      label: 'Total Amount',
      value: formatCurrency(subtotalAmount),
      title: formatExactCurrency(subtotalAmount),
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-700'
    },
    {
      label: 'Shipping Charge',
      value: formatCurrency(shippingCharge),
      title: formatExactCurrency(shippingCharge),
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-700'
    },
  ];

  const itemData = Array.isArray(order.items) ? order.items : [];

  const filteredItemData = useMemo(() => {
    if (!itemData || itemData.length === 0) return [];
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return itemData;
    }
    return itemData.filter((item) => {
      const pool = [
        item.name,
        item.sku,
        item.unit,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return pool.some((value) => value.includes(normalizedSearch));
    });
  }, [itemData, searchValue]);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: SorterResult<FrontsiteOrderItem> | SorterResult<FrontsiteOrderItem>[],
  ) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (normalizedSorter?.order && normalizedSorter.columnKey) {
      setSortState({
        key: String(normalizedSorter.columnKey),
        order: normalizedSorter.order,
      });
    } else {
      setSortState({});
    }
  };

  const itemColumns = useMemo<ColumnsType<FrontsiteOrderItem>>(() => {
    const compareString = (a?: string | number | null, b?: string | number | null) => {
      const left = (a ?? '').toString().toLowerCase();
      const right = (b ?? '').toString().toLowerCase();
      if (left < right) return -1;
      if (left > right) return 1;
      return 0;
    };

    return [
      {
        title: 'Product',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => compareString(a.name, b.name),
        sortOrder: sortState.key === 'name' ? sortState.order : undefined,
        render: (value: string | undefined) => value || 'N/A',
      },
      {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        align: 'center',
        sorter: (a, b) => compareString(a.sku, b.sku),
        sortOrder: sortState.key === 'sku' ? sortState.order : undefined,
        render: (value: string | undefined) => value || 'N/A',
      },
      {
        title: 'Quantity',
        dataIndex: 'qty',
        key: 'qty',
        align: 'center',
        sorter: (a, b) => (Number(a.qty) || 0) - (Number(b.qty) || 0),
        sortOrder: sortState.key === 'qty' ? sortState.order : undefined,
        render: (value: number | undefined) => (value ?? 0).toLocaleString(),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        align: 'center',
        sorter: (a, b) => compareString(a.unit, b.unit),
        sortOrder: sortState.key === 'unit' ? sortState.order : undefined,
        render: (value: string | undefined) => value || 'N/A',
      },
      {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        align: 'right',
        sorter: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
        sortOrder: sortState.key === 'price' ? sortState.order : undefined,
        render: (value: number | undefined) => formatCurrency(value ?? 0),
      },
      {
        title: 'Subtotal',
        dataIndex: 'subtotal',
        key: 'subtotal',
        align: 'right',
        sorter: (a, b) => (Number(a.subtotal) || 0) - (Number(b.subtotal) || 0),
        sortOrder: sortState.key === 'subtotal' ? sortState.order : undefined,
        render: (value: number | undefined) => formatCurrency(value ?? 0),
      },
    ];
  }, [formatCurrency, sortState]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row justify-between items-start gap-3">
        <div>
          <p className="text-slate-500 uppercase tracking-wide text-xs font-medium">
            Order Details
          </p>
          <h5 className="font-bold text-slate-900 text-lg mb-0">
            {order.so_number || `Order #${order.order_id || order.id}`}
          </h5>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tag
            color={(order.payment_status || '').toLowerCase() === 'completed' ? 'success' : 'warning'}
            className="font-semibold text-sm"
          >
            {order.payment_status ? order.payment_status.toUpperCase() : 'N/A'}
          </Tag>
          <h5 className="font-bold text-green-700 text-lg mb-0" title={formatExactCurrency(totalAmount)}>
            {formatCurrency(totalAmount)}
          </h5>
        </div>
      </div>

      <div>
        <p className="text-slate-900 font-semibold text-base mb-2 block">
          Order Information
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {infoCards.map((card) => (
            <div
              key={`${order.order_id}-${card.label}`}
              className={`${card.bgColor} rounded p-3 min-h-[72px]`}
              title={card.title}
            >
              <p className={`${card.textColor} uppercase tracking-wide text-xs font-medium mb-1`}>
                {card.label}
              </p>
              <p className={`${card.textColor} font-semibold text-base block`}>
                {card.value || 'N/A'}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-4">
          <p className="text-slate-900 font-semibold text-base mb-0 mb-2">
            Order Items
          </p>
          {itemData.length > 0 && (
            <Input
              placeholder="Search by product, SKU, unit..."
              allowClear
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              size="large"
              suffix={<SearchOutlined className="text-lg !text-blue-600" />}
              className="w-full sm:!w-96 !rounded"
            />
          )}
        </div>
        {itemData.length > 0 ? (
          <Table<FrontsiteOrderItem>
            columns={itemColumns}
            dataSource={filteredItemData}
            rowKey={(item, index) => `${order.order_id || order.id}-item-${item.id || index}`}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: filteredItemData.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
              className: '!px-4'
            }}
            onChange={handleTableChange}
            className='border'
            size="small"
            locale={{
              emptyText: searchValue
                ? `No items match "${searchValue}".`
                : 'No items available for this order.'
            }}
          />
        ) : (
          <div className="border border-dashed border-slate-200 rounded p-4 text-slate-500 text-sm font-normal">
            No order items were returned for this entry.
          </div>
        )}
      </div>
    </div>
  );
};

export default FrontsiteOrderDetails;
